import React, { useState, useEffect, useRef } from 'react'
import {
  EnvelopeIcon,
  PhoneIcon,
  ArrowPathIcon,
  XMarkIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/solid'
import toast from 'react-hot-toast'

interface VerificationInputProps {
  type: 'email' | 'phone'
  identifier: string
  purpose: 'register' | 'login' | 'verify'
  onVerifySuccess: () => void
  onCancel?: () => void
}

const VerificationInput: React.FC<VerificationInputProps> = ({
  type,
  identifier,
  purpose,
  onVerifySuccess,
  onCancel
}) => {
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [isLoading, setIsLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [canResend, setCanResend] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // 初始发送验证码
  useEffect(() => {
    sendVerificationCode()
  }, [])

  // 倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [countdown])

  // 自动提交验证码
  useEffect(() => {
    const fullCode = code.join('')
    if (fullCode.length === 6) {
      handleVerify(fullCode)
    }
  }, [code])

  const sendVerificationCode = async () => {
    try {
      setIsLoading(true)
      const response = await fetch('/api/auth/send-verification-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          identifier,
          type: purpose
        })
      })

      const result = await response.json()

      if (response.ok) {
        toast.success(`验证码已发送到${type === 'email' ? '邮箱' : '手机'}`)
        setCountdown(60) // 60秒倒计时
        setCanResend(false)
      } else {
        toast.error(result.message || '发送失败')
      }
    } catch (error) {
      console.error('发送验证码失败:', error)
      toast.error('发送验证码失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify = async (verificationCode: string) => {
    if (!verificationCode || verificationCode.length !== 6) {
      return
    }

    try {
      setIsLoading(true)
      const endpoint = type === 'email' ? '/api/auth/verify-email' : '/api/auth/verify-phone'

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          [type === 'email' ? 'email' : 'phone']: identifier,
          code: verificationCode,
          type: purpose
        })
      })

      const result = await response.json()

      if (response.ok) {
        setIsVerified(true)
        toast.success('验证成功！')
        setTimeout(() => {
          onVerifySuccess()
        }, 1000)
      } else {
        toast.error(result.message || '验证失败')
        // 清空输入
        setCode(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
      }
    } catch (error) {
      console.error('验证失败:', error)
      toast.error('验证失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = () => {
    if (canResend) {
      sendVerificationCode()
    }
  }

  const handleInputChange = (index: number, value: string) => {
    // 只允许数字
    const digit = value.replace(/\D/g, '').slice(-1)

    const newCode = [...code]
    newCode[index] = digit
    setCode(newCode)

    // 自动跳到下一个输入框
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData) {
      const newCode = [...code]
      pastedData.split('').forEach((char, i) => {
        if (i < 6) newCode[i] = char
      })
      setCode(newCode)
      inputRefs.current[Math.min(pastedData.length, 5)]?.focus()
    }
  }

  const maskIdentifier = (str: string) => {
    if (type === 'email') {
      const [local, domain] = str.split('@')
      return `${local.slice(0, 2)}***@${domain}`
    } else {
      return `${str.slice(0, 3)}****${str.slice(-4)}`
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300">
        {/* 顶部装饰 */}
        <div className="h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

        <div className="p-8">
          {/* 图标 */}
          <div className="text-center mb-6">
            <div className="relative inline-flex">
              <div className="absolute inset-0 bg-indigo-500/20 rounded-full blur-xl animate-pulse" />
              <div
                className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-500 ${
                  isVerified
                    ? 'bg-gradient-to-br from-emerald-400 to-teal-500'
                    : 'bg-gradient-to-br from-indigo-500 to-purple-600'
                }`}
              >
                {isVerified ? (
                  <CheckCircleIcon className="w-10 h-10 text-white animate-in zoom-in duration-300" />
                ) : type === 'email' ? (
                  <EnvelopeIcon className="w-10 h-10 text-white" />
                ) : (
                  <PhoneIcon className="w-10 h-10 text-white" />
                )}
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-5">
              {isVerified ? '验证成功！' : type === 'email' ? '邮箱验证' : '短信验证'}
            </h3>
            <p className="text-slate-500 mt-2 text-sm">
              {isVerified ? (
                '正在跳转...'
              ) : (
                <>
                  验证码已发送至{' '}
                  <span className="font-semibold text-slate-700">{maskIdentifier(identifier)}</span>
                </>
              )}
            </p>
          </div>

          {!isVerified && (
            <>
              {/* 验证码输入 */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-3 text-center">
                  请输入 6 位验证码
                </label>
                <div className="flex justify-center gap-2" onPaste={handlePaste}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all ${
                        digit
                          ? 'border-indigo-400 bg-indigo-50'
                          : 'border-slate-200 bg-slate-50'
                      }`}
                      autoFocus={index === 0}
                      disabled={isLoading}
                    />
                  ))}
                </div>
              </div>

              {/* 重发按钮 */}
              <div className="flex justify-center items-center gap-4 text-sm mb-6">
                <span className="text-slate-500">
                  {countdown > 0 ? `${countdown}s 后可重新发送` : '没收到验证码？'}
                </span>
                <button
                  onClick={handleResend}
                  disabled={!canResend || isLoading}
                  className={`inline-flex items-center gap-1.5 font-semibold ${
                    canResend && !isLoading
                      ? 'text-indigo-600 hover:text-indigo-700'
                      : 'text-slate-400 cursor-not-allowed'
                  }`}
                >
                  <ArrowPathIcon
                    className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
                  />
                  重新发送
                </button>
              </div>

              {/* 操作按钮 */}
              <div className="flex gap-3">
                {onCancel && (
                  <button
                    onClick={onCancel}
                    className="flex-1 px-5 py-3.5 border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-all disabled:opacity-50"
                    disabled={isLoading}
                  >
                    取消
                  </button>
                )}
                <button
                  onClick={() => handleVerify(code.join(''))}
                  disabled={code.join('').length !== 6 || isLoading}
                  className="flex-1 px-5 py-3.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg shadow-indigo-200/50 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      验证中...
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <ShieldCheckIcon className="w-5 h-5" />
                      验证
                    </span>
                  )}
                </button>
              </div>
            </>
          )}

          {/* 安全提示 */}
          {!isVerified && (
            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-400">
              <SparklesIcon className="w-4 h-4 text-indigo-400" />
              <span>验证码 5 分钟内有效，请勿泄露给他人</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VerificationInput
